import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Employee, FormTemplate } from '@employ-engage/data';
import {
  InlineLoadingSpinner,
  RichTextRenderer,
  GET_FORM_BY_ID_AND_COMPANY_ID,
  GET_FORM_VERSION_NO_AUTH,
  GET_FORM_RESPONSES_FOR_CURRENT_USER_AND_COMPANY_NO_AUTH,
  GET_EMPLOYEE_BY_ID_AND_COMPANY_ID,
  CREATE_FORM_RESPONSE_NO_AUTH,
} from '@employ-engage/common-components';
import Delta from 'quill-delta';
import PasscodeForm from './passcode-form';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { useQuery, useApolloClient, useMutation } from '@apollo/client';

export function BeginSurvey() {
  const { t } = useTranslation(['common']);
  const [employee, setEmployee] = useState<Employee | null>(null);
  const { formId, employeeId } = useParams();
  const navigate = useNavigate();
  const [passcodeRequiredStatus, setPasscodeRequiredStatus] = useState<
    'pending' | 'required' | 'not-required'
  >('pending');
  const [passcodeValidityStatus, setPasscodeValidityStatus] = useState<
    'pending' | 'valid' | 'invalid'
  >('pending');
  const [currentTemplateVersion, setCurrentTemplateVersion] =
    useState<FormTemplate>();
  const [welcomePageText, setWelcomePageText] = useState<Delta | null>(null);
  const apolloClient = useApolloClient();
  const { pathname } = useLocation();
  const friendlyCompanyId = pathname.split('/')[1];
  const { data: { formByIdAndCompanyIdNoAuth: form } = {} } = useQuery(
    GET_FORM_BY_ID_AND_COMPANY_ID,
    {
      variables: { id: formId, friendlyCompanyId },
      fetchPolicy: 'network-only',
    }
  );

  const [createFormResponseNoAuth] = useMutation(CREATE_FORM_RESPONSE_NO_AUTH);

  useEffect(() => {
    if (form) {
      apolloClient
        .query({
          query: GET_FORM_VERSION_NO_AUTH,
          variables: { id: form.templateVersionId, companyId: form.companyId },
          fetchPolicy: 'network-only',
        })
        .then(
          async ({ data: { formVersionNoAuth: formVersion } = {} }: any) => {
            if (formVersion) {
              if (formVersion.isPasscodeRequired) {
                setPasscodeRequiredStatus('required');
              } else {
                setPasscodeRequiredStatus('not-required');
              }
              setCurrentTemplateVersion(formVersion as unknown as FormTemplate);
              setWelcomePageText(formVersion?.welcomePageRichText || null);

              const { data: { employeeByEmployeeIdAndCompanyIdNoAuth } = {} } =
                await apolloClient.query({
                  query: GET_EMPLOYEE_BY_ID_AND_COMPANY_ID,
                  variables: {
                    employeeId: employeeId,
                    companyId: form.companyId,
                  },
                  fetchPolicy: 'network-only',
                });

              setEmployee(employeeByEmployeeIdAndCompanyIdNoAuth);
            }
          }
        );
    }
  }, [form, apolloClient, employeeId]);

  const handleStartSurvey = async () => {
    if (!form || !employeeId || !formId) return;

    // 1. Query for existing incomplete form responses for this employee and form
    const { data: { formResponsesNoAuth: formResponses } = {} } =
      await apolloClient.query({
        query: GET_FORM_RESPONSES_FOR_CURRENT_USER_AND_COMPANY_NO_AUTH,
        variables: {
          input: {
            employeeId,
            companyId: form.companyId,
          },
        },
        fetchPolicy: 'network-only',
      });

    // 2. Filter for responses that are not completed and not expired
    const now = new Date();
    const validResponses = (formResponses || []).filter((resp: any) => {
      return (
        !resp.dateCompleted &&
        (!resp.form?.closeDate || new Date(resp.form.closeDate) > now)
      );
    });

    if (validResponses.length > 0) {
      if (validResponses.length > 1) {
        throw new Error('More than one response document found');
      }
      const response = validResponses[0];
      navigate(
        `/${friendlyCompanyId}/form/${formId}/employee/${employeeId}/response/${response.id}/page/0`,
        { state: { token: uuidv4() } }
      );
      return;
    }

    // 3. If no valid response, create a new one
    const { data: { createFormResponseNoAuth: newResponse } = {} } =
      await createFormResponseNoAuth({
        variables: {
          input: {
            surveyId: formId,
            employeeId,
            companyId: form.companyId,
            pages: [],
            employeeDepartmentWhenCreated: employee?.department,
          },
        },
      });

    if (newResponse) {
      navigate(
        `/${friendlyCompanyId}/form/${formId}/employee/${employeeId}/response/${newResponse.id}/page/0`
      );
    }
  };

  if (passcodeRequiredStatus === 'pending') {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <h1 className="text-3xl font-bold leading-9 text-white">
          {t('Loading...')}
        </h1>
        <InlineLoadingSpinner />
      </div>
    );
  }

  if (
    passcodeRequiredStatus === 'required' &&
    passcodeValidityStatus === 'pending'
  ) {
    if (employeeId && formId) {
      return (
        <PasscodeForm
          employeeId={employeeId}
          formId={formId}
          onFailedPasscode={() => setPasscodeValidityStatus('invalid')}
          onValidPasscode={() => setPasscodeValidityStatus('valid')}
        />
      );
    } else {
      return <div>{t('Loading...')}</div>;
    }
  }

  if (
    passcodeValidityStatus === 'invalid' &&
    passcodeRequiredStatus === 'required'
  ) {
    return (
      <div className="flex flex-col items-center justify-center">
        <h1 className="text-3xl font-bold leading-9 text-white">
          {t('Invalid passcode')}
        </h1>
      </div>
    );
  }

  if (
    passcodeValidityStatus === 'valid' ||
    passcodeRequiredStatus === 'not-required'
  ) {
    return (
      <div>
        {!!employee && (
          <>
            <div className="flex justify-center">
              <h1 className="text-4xl font-bold leading-9 text-white">
                {t('Hi')}
                {currentTemplateVersion?.isAnonymous
                  ? ''
                  : ` ${employee?.firstName}`}
                !
              </h1>
            </div>
            <div className="h-1 w-20 bg-white/50 mx-auto my-4"></div>
          </>
        )}
        {welcomePageText && <RichTextRenderer content={welcomePageText} />}
        <div className="flex justify-center mt-10 sm:mt-20">
          <button
            onClick={handleStartSurvey}
            className="bg-light-primary dark:bg-dark-primary hover:bg-dark-secondaryAccent text-white dark:text-custom-gray600 font-bold py-2 px-4 rounded"
          >
            {t('Begin')}
          </button>
        </div>{' '}
      </div>
    );
  }

  return <div>{t('Loading...')}</div>;
}

export default BeginSurvey;
